import { useEffect, useState } from 'react'

import { getAxiosInstance } from 'services/api'

export type FeatureFlags = {
  regionTeams: boolean
}

const envFallback = (): FeatureFlags => ({
  regionTeams: import.meta.env.VITE_REGION_TEAMS_ENABLED === 'true',
})

// Module-level cache: one request per page load, shared by every caller.
let cached: FeatureFlags | null = null
let inflight: Promise<FeatureFlags> | null = null

export const fetchFeatureFlags = (): Promise<FeatureFlags> => {
  if (cached) return Promise.resolve(cached)
  if (!inflight) {
    inflight = getAxiosInstance()
      .get('/features')
      .then((res) => {
        const data = res?.data ?? {}
        const fallback = envFallback()
        return {
          regionTeams: typeof data.regionTeams === 'boolean' ? data.regionTeams : fallback.regionTeams,
        }
      })
      .catch(() => envFallback())
      .then((flags) => {
        cached = flags
        return flags
      })
  }
  return inflight
}

/** Test helper: forget the cached flags. */
export const resetFeatureFlagsCache = () => {
  cached = null
  inflight = null
}

/**
 * Server feature flags (GET /features). Falls back to VITE_* env flags when the
 * endpoint is missing or fails. `loading` is true until the first answer.
 * Pass `enabled=false` to defer the request (nothing is fetched until it is true).
 */
export const useFeatureFlags = (enabled = true) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => cached ?? envFallback())
  const [loading, setLoading] = useState(!cached)

  useEffect(() => {
    // Callers mounted app-wide pass enabled=false until the flags are needed, so
    // anonymous page loads never hit the endpoint.
    if (!enabled) return
    let active = true
    fetchFeatureFlags().then((next) => {
      if (!active) return
      setFlags(next)
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [enabled])

  return { ...flags, loading }
}
